import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SITE } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_TYPES = [
  "Standard — Exterior",
  "Standard — Interior",
  "Standard — Interior & Exterior",
  "Commercial — Exterior",
  "Commercial — Interior",
  "Commercial — Interior & Exterior",
  "Solar panels",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  address: "",
  window_count: "",
  screen_count: "",
  service_type: SERVICE_TYPES[0],
  notes: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const buildSmsBody = () => {
    return [
      `New quote request — Vision Glass Care`,
      ``,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.email ? `Email: ${form.email}` : null,
      `Address: ${form.address}`,
      `# of windows: ${form.window_count}`,
      form.screen_count
        ? `# of screens (add-on): ${form.screen_count}`
        : null,
      `Service: ${form.service_type}`,
      form.notes ? `Notes: ${form.notes}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim() || !form.window_count.trim()) {
      toast.error("Please fill in name, phone, address, and window count.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request sent!", {
        description: "We've emailed Vision Glass Care your details. Expect a reply within an hour during business hours.",
      });
      setForm(initial);
    } catch (err) {
      console.warn("Quote submission failed:", err?.message || err);
      toast.error("Hmm, something went wrong sending that.", {
        description: "Please try again or call (225) 777-9747.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute -top-32 right-0 h-[420px] w-[420px] rounded-full opacity-30"
          style={{
            background:
              "radial-gradient(circle, rgba(0,180,216,0.35), rgba(0,180,216,0) 60%)",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
        {/* Left info */}
        <div className="lg:col-span-5">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#00B4D8] font-bold">
            Free, no-pressure quote
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-[#0B2542] leading-tight">
            Tell us how many windows.
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Submit the form and we'll text you back with a clear, fixed price — usually
            within an hour during business hours.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${SITE.phoneTel}`}
              className="flex items-center gap-4 rounded-2xl border border-blue-100 bg-white p-5 hover:border-[#00B4D8]/50 transition-colors"
              data-testid="contact-phone-card"
            >
              <div className="h-11 w-11 rounded-xl bg-[#0F4C81] text-white flex items-center justify-center">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Call us
                </div>
                <div className="font-[Outfit] font-bold text-[#0B2542] text-lg">
                  {SITE.phoneDisplay}
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4 rounded-2xl border border-blue-100 bg-white p-5">
              <div className="h-11 w-11 rounded-xl bg-[#00B4D8] text-white flex items-center justify-center">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Hours
                </div>
                <div className="font-[Outfit] font-bold text-[#0B2542] text-lg">
                  {SITE.hours}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 rounded-2xl border border-blue-100 bg-white p-5">
              <div className="h-11 w-11 rounded-xl bg-[#0B2542] text-white flex items-center justify-center">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Service area
                </div>
                <div className="font-[Outfit] font-bold text-[#0B2542] text-lg">
                  Baton Rouge & surrounding parishes
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="quote-form"
            className="rounded-[28px] bg-white border border-blue-100 vgc-card-shadow p-6 sm:p-8 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <Input
                  data-testid="quote-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  className="h-12 rounded-xl border-blue-100 focus-visible:ring-[#00B4D8]"
                />
              </Field>
              <Field label="Phone *">
                <Input
                  data-testid="quote-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(225) 555-1234"
                  className="h-12 rounded-xl border-blue-100 focus-visible:ring-[#00B4D8]"
                />
              </Field>
              <Field label="Email (optional)">
                <Input
                  data-testid="quote-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="h-12 rounded-xl border-blue-100 focus-visible:ring-[#00B4D8]"
                />
              </Field>
              <Field label="Service address *">
                <Input
                  data-testid="quote-address"
                  required
                  value={form.address}
                  onChange={update("address")}
                  placeholder="123 Main St, Baton Rouge"
                  className="h-12 rounded-xl border-blue-100 focus-visible:ring-[#00B4D8]"
                />
              </Field>
              <Field label="# of windows *">
                <Input
                  data-testid="quote-window-count"
                  required
                  inputMode="numeric"
                  value={form.window_count}
                  onChange={update("window_count")}
                  placeholder="e.g. 18"
                  className="h-12 rounded-xl border-blue-100 focus-visible:ring-[#00B4D8]"
                />
              </Field>
              <Field label="# of screens (add-on)">
                <Input
                  data-testid="quote-screen-count"
                  inputMode="numeric"
                  value={form.screen_count}
                  onChange={update("screen_count")}
                  placeholder="optional — priced per screen"
                  className="h-12 rounded-xl border-blue-100 focus-visible:ring-[#00B4D8]"
                />
              </Field>
              <Field label="Service type">
                <Select
                  value={form.service_type}
                  onValueChange={(v) => setForm((s) => ({ ...s, service_type: v }))}
                >
                  <SelectTrigger
                    className="h-12 rounded-xl border-blue-100 focus:ring-[#00B4D8]"
                    data-testid="quote-service-type"
                  >
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-2 text-sm text-slate-600">
                  Frames, sills &amp; tracks included on every standard exterior
                  package. Interior cleaning is an add-on.
                </p>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Anything we should know? (optional)">
                <Textarea
                  data-testid="quote-notes"
                  value={form.notes}
                  onChange={update("notes")}
                  placeholder="2-story home, front bay window has hard water spots, dog in backyard, etc."
                  rows={4}
                  className="rounded-xl border-blue-100 focus-visible:ring-[#00B4D8] resize-none"
                />
              </Field>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[#0F4C81] text-white font-semibold hover:bg-[#0B3A66] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Mail className="h-5 w-5" />
                )}
                Send Quote Request
              </button>
              <p className="text-xs text-slate-500 max-w-sm">
                We'll receive your request by email and reply within an hour during
                business hours. No spam, no sharing — your info goes only to Vision
                Glass Care.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
